import PropTypes from 'prop-types';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import { useAuth } from 'contexts/auth';
import { useCallback, useEffect, useState } from 'react';
import { getLocaleMap } from 'utils/locales';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleErrorResponse } from 'utils/general';
import { useDefaultListSize, listSizeOptions } from 'utils/listSize';
import { Autocomplete, Dialog, DialogActions, DialogContent, Divider, InputBase, Paper, Tab, Tabs, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import moment from 'moment';
import DataTable from 'components/DataTable';
import { withLoading } from 'utils/hoc';
import get from 'lodash/get';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import filter from 'lodash/filter';
import some from 'lodash/some';
import cloneDeep from 'lodash/cloneDeep';
import sortedUniq from 'lodash/sortedUniq';
import escapeRegExp from 'lodash/escapeRegExp';
import getFp from 'lodash/fp/get';
import pickFp from 'lodash/fp/pick';
import CloseIcon from '@mui/icons-material/Close';
import Button from 'components/Button';
import Select from 'components/Select';
import Image from 'components/Image';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getBrands, getMergedUserProducts, getModels, getSources, getUserProducts } from 'api/monitors';
import { getUserIWatchAccounts } from 'api/iwatch';
import { multiWordfilterOptions } from 'components/AutoComplete';
import { parsePhoneNumberFromString, ParseError } from 'libphonenumber-js/max';

const UserProductListSection = ({ title_font_size, header_color, text_color, show_header, extra_params, page_options, sort_by_options, currency_options, source_options, setLoading, ...props }) => {
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userProductIdParamStr = searchParams.get('user_product_id');
  const defaultListSize = useDefaultListSize(page_options);
  const [pageSize, setPageSize] = useState(defaultListSize);
  useEffect(() => {
    setPageSize(defaultListSize);
  }, [defaultListSize]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [keyword, setKeyword] = useState();
  const [rowsData, setRowsData] = useState([]);

  const [tabValue, setTabValue] = useState('sell');
  const [product, setProduct] = useState();
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);

  const [brandFilter, setBrandFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState();
  const [currencyFilter, setCurrencyFilter] = useState();
  const [filterParams, setFilterParams] = useState({});

  const [sortByFilter, setSortByFilter] = useState('createddate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  const [brandOptions, setBrandOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sortByOptions, setSortByOptions] = useState([]);

  const [keywordOptions, setKeywordOptions] = useState([]);
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [allModelOptions, setAllModelOptions] = useState([]);
  const [filterModelOptions, setFilterModelOptions] = useState([]);
  const [models, setModels] = useState([]);

  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap([])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const handleShowProductDetail = useCallback((data) => {
    setProduct(data);
    setIsProductDialogOpen(true);
  }, []);

  const handleClickContact = useCallback((e, data) => {
    e.stopPropagation();
    const cn = data.contact_number;
    if (cn) {
      const contactNumber = get(cn, 'phone', cn);
      const contactName = get(cn, 'author', data.contact_name);
      navigate(`/contact_detail?contact_name=${encodeURIComponent(contactName)}&contact_number=${encodeURIComponent(contactNumber)}`);
    }
  }, [navigate]);

  const columns = [
    {
      field: 'product_id',
      sortable: false,
      renderContent: (data) => {
        const { brand, model, description, condition, price, currency, image_url, contact_name, contact_number, createddate, model_id, year, month } = data;
        // eslint-disable-next-line no-return-assign, no-param-reassign
        const contactName = contact_name?.split(/\s/).reduce((response, word) => response += word.slice(0, 1).toUpperCase(), '');
        const whatsappMessage = `${tabValue} ${model} ${currency} ${price?.toLocaleString()}`;
        const productTags = map(get(data, 'product_tags', []), 'tag');
        if (!some(productTags, (tag) => 'new' === tag || 'used' === tag)) {
          productTags.unshift(condition);
        }
        const modelIdName = get(model_id, 'id_name');
        const modelBrandName = get(model_id, 'brand_name');
        return (
          <MKBox mb={1} padding="0.5rem" maxWidth="100%" style={{ cursor: 'pointer' }}>
            <MKBox width="100%" padding={1} style={{ border: '1px solid lightgray' }} display="flex" flexDirection="column" onClick={() => handleShowProductDetail(data)}>
              <MKBox display="flex" flexDirection="column">
                <MKTypography mb={1} fontSize="16px" color={text_color}>
                  {modelIdName}
                </MKTypography>
                <MKTypography mb={1} fontSize="16px" color="gray">
                  {modelBrandName}
                </MKTypography>
              </MKBox>
              <Divider sx={{ bgcolor: 'gray', margin: 0, marginBottom: '1rem' }} />
              <MKBox display="flex">
                <MKBox display="flex" flex={1} mr="1rem">
                  <Image src={image_url} width="100%" style={{ cursor: 'pointer' }} />
                </MKBox>
                <MKBox display="flex" flex={3} flexDirection="column" justifyContent="space-between">
                  <MKTypography mb={1} fontSize="16px" color={text_color}>
                    {description}
                  </MKTypography>
                  <MKBox display="flex">
                    {/*
                    {!(typeof condition !== 'string' || condition.length === 0) && (
                      <MKTypography fontSize="12px" color={text_color} padding="0.1rem 0.5rem" sx={{ backgroundColor: header_color, marginRight: '0.5rem' }}>
                        {condition.charAt(0).toUpperCase() + condition.slice(1)}
                      </MKTypography>
                    )}
                    {year && (
                      <MKTypography fontSize="12px" color={text_color} padding="0.1rem 0.5rem" sx={{ backgroundColor: header_color, marginRight: '0.5rem' }}>
                        {year}
                      </MKTypography>
                    )}
                    {month && (
                      <MKTypography fontSize="12px" color={text_color} padding="0.1rem 0.5rem" sx={{ backgroundColor: header_color, marginRight: '0.5rem' }}>
                        {month}
                      </MKTypography>
                    )}
                    */}
                    {productTags.map((tag) => {
                      return <MKTypography fontSize="12px" color={text_color} padding="0.1rem 0.5rem" sx={{ backgroundColor: header_color, marginRight: '0.5rem' }}>
                        {tag}
                      </MKTypography>
                    })}
                  </MKBox>
                </MKBox>
                <MKBox display="flex" flex={1.5} flexDirection="column" alignItems="flex-end">
                  <MKBox display="flex">
                    <MKBox onClick={(e) => handleClickContact(e, data)} bgColor="#3259a1" color="white" display="flex" justifyContent="center" alignItems="center" padding={1} sx={{ borderRadius: '50%', cursor: 'pointer' }} width="40px" height="40px" mr="0.5rem">
                      {contactName}
                    </MKBox>
                    <WhatsAppIcon
                      fontSize="large"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`//api.whatsapp.com/send?phone=${encodeURIComponent(contact_number)}&text=${encodeURIComponent(whatsappMessage)}`, '_blank');
                      }}
                    />
                  </MKBox>
                  <MKTypography mb={1} fontSize="16px" color={text_color} fontWeight="bold" textAlign="right">
                    {`${currency} ${price.toLocaleString()}`}
                  </MKTypography>
                  <MKTypography fontSize="14px" color="gray" textAlign="right">
                    {moment(createddate).fromNow()}
                  </MKTypography>
                </MKBox>
              </MKBox>
            </MKBox>
          </MKBox>
        );
      },
    },
  ];

  const handleChangeTab = useCallback((event, newTabValue) => {
    setTabValue(newTabValue);
  }, []);

  const fetchUserProductsFromApi = useCallback(() => {
    setLoading(true);
    const dataParams = {
      $orderBy: `${sortByFilter} ${sortDirection}`,
      $skip: currentPage * pageSize,
      $top: pageSize,
      $expand: 'product/model_id/default_image,product/product_tags',
      user: auth?.user?.username,
      ...extra_params,
      // ...(keyword && { keyword }),
      ...filterParams,
    };

    if (keyword) {
      const keywordPattern = new RegExp(escapeRegExp(keyword), 'i');
      let matchedModelIds = filter(models, (model) => {
        const idName = model.id_name;
        return idName && keywordPattern.test(idName);
      }).map(getFp('model_id'));

      let newModelIdAdded = false;

      const existingModelId = dataParams['product/model_id'];
      if (existingModelId) {
        matchedModelIds.push(existingModelId);
        newModelIdAdded = true;
      }

      const existingModelIds = dataParams['product/model_id[in]'];
      if (existingModelIds) {
        const existingModelIdsArray = existingModelIds.split(',');
        if (existingModelIdsArray.length > 0) {
          Array.prototype.push.apply(matchedModelIds, existingModelIdsArray);
          newModelIdAdded = true;
        }
      }

      if (newModelIdAdded) {
        matchedModelIds.sort();
        matchedModelIds = sortedUniq(matchedModelIds);
      }

      if (matchedModelIds && matchedModelIds.length > 0) {
        dataParams['product/model_id[in]'] = matchedModelIds.join();
      }
    }

    if (['sell', 'buy'].includes(tabValue)) {
      dataParams['product/monitor_type'] = tabValue;
    } else {
      dataParams['product/condition'] = tabValue;
      dataParams['product/monitor_type'] = 'sell';
    }

    if (userProductIdParamStr) {
      const userProductIds = userProductIdParamStr.split(',');
      if (userProductIds.length > 0) {
        dataParams['user_product_id[in]'] = userProductIdParamStr;
      }
    }

    return getMergedUserProducts(dataParams)
      .then(({ data, headers }) => {
        const contentRange = get(headers, 'content-range');
        const size = Number(contentRange.split('/')[1]);
        setRowsData(data.map((datum) => datum.product));
        setTotalRows(size);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, sortByFilter, sortDirection, currentPage, pageSize, auth?.user?.username, extra_params, keyword, filterParams, tabValue, setAuth, userProductIdParamStr]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage, pageSize, sortByFilter, sortDirection, keyword, filterParams, tabValue]);

  useEffect(() => {
    fetchUserProductsFromApi();
  }, [fetchUserProductsFromApi]);

  const handleConfirmFilter = useCallback(() => {
    setIsFilterDialogOpen(false);

    let matchedModelIds = null;
    if (modelFilter && modelFilter !== 'all') {
      matchedModelIds = filter(models, (model) => {
        const idName = model.id_name;
        return idName && idName.includes(modelFilter);
      }).map(getFp('model_id'));
    }

    const newFilteredParams = {};
    if (matchedModelIds && matchedModelIds.length > 0) {
      newFilteredParams['product/model_id[in]'] = matchedModelIds.join();
    }
    if (brandFilter && brandFilter !== 'all' && !newFilteredParams['model_id[in]']) {
      // only set brand filter if model is not set since these 2 values may not in sync
      // in some prodcuts
      const brand = brandOptions.find(brandOption => brandOption.value === brandFilter);
      const brandName = brand && brand.label;
      if (brandName && brandName.length > 0) {
        newFilteredParams['product/brand'] = brandFilter;
      }
    }
    if (currencyFilter && currencyFilter.length > 0) {
      newFilteredParams['product/currency[in]'] = currencyFilter.join();
    }
    if (sourceFilter && sourceFilter.length > 0) {
      newFilteredParams['product/account[in]'] = sourceFilter.join();
    }
    setFilterParams(newFilteredParams);
    setCurrentPage(0);
  }, [brandFilter, currencyFilter, modelFilter, sourceFilter]);

  const handleResetFilter = useCallback(() => {
    setIsFilterDialogOpen(false);
    setFilterParams({});
    setBrandFilter('all');
    setModelFilter('all');
    setCurrencyFilter(null);
    setSourceFilter(null);
    setValue(null);
    setInputValue('');
    setSortByFilter('createddate');
    setSortDirection('desc');
    setCurrentPage(0);
  }, []);

  const fetchBrandsFromApi = useCallback(() => {
    return getBrands()
      .then(({ data }) => {
        const options = data.map((datum) => ({
          // value: datum.name,
          value: datum.brand_id,
          label: datum.name,
        }));
        setBrandOptions([{ value: 'all', label: 'All' }, ...options]);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [setAuth]);

  const fetchModelsFromApi = useCallback(() => {
    return getModels({ $orderby: 'id_name' })
      .then(({ data }) => {
        const pickFn = pickFp(['id_code', 'id_name', 'brand_name', 'brand']);
        const newAllModelOptions = map(data, pickFn);
        const newKeywordOptions = cloneDeep(newAllModelOptions);
        setModels(data);
        setAllModelOptions(newAllModelOptions);
        setKeywordOptions(newKeywordOptions);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [setAuth]);

  const fetchSourcesFromApi = useCallback(() => {
    return getSources()
      .then(({ data }) => {
        setSourceOptions(data.map((datum) => {
          return {
            value: datum.name,
            label: datum.name,
          };
        }));
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [setAuth]);

  const fetchUserIWatchAccountsFromApi = useCallback(() => {
    if (!auth?.user) {
      return Promise.resolve();
    }
    return getUserIWatchAccounts({ $expand: 'iwatch_account', user: auth?.user?.username })
      .then(({ data }) => {
        const iWatchAccounts = sortBy(map(data, 'iwatch_account'), ['sequence']);
        const newSourceAccountOptions = map(iWatchAccounts, (iWatchAccount) => {
          const accountName = iWatchAccount.name;
          const phone = iWatchAccount.phone;
          try {
            const phoneNumber = parsePhoneNumberFromString('+' + phone);
            const formattedPhoneNumber = phoneNumber && phoneNumber.formatInternational() || phone;
            return {
              label: `${iWatchAccount.key} (${accountName}) (${formattedPhoneNumber})`,
              value: accountName,
            };
          } catch (e) {
            if (e instanceof ParseError) {
              // Not a phone number, non-existent country, etc.
              console.log(e.message);
            } else {
              throw e;
            }
          }
        });

        // newSourceAccountOptions.unshift({ label: '(none)', value: null });
        setSourceOptions(newSourceAccountOptions);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [auth, setAuth]);

  useEffect(() => {
    fetchBrandsFromApi();
    fetchModelsFromApi();
    fetchUserIWatchAccountsFromApi();
  }, [fetchBrandsFromApi, fetchModelsFromApi]);

  useEffect(() => {
    if (currency_options) {
      setCurrencyOptions(currency_options);
    }
  }, [currency_options]);

  useEffect(() => {
    if (sort_by_options) {
      setSortByOptions(sort_by_options);
    }
  }, [sort_by_options]);

  // useEffect(() => {
  //   if (source_options) {
  //     setSourceOptions(source_options);
  //   }
  // }, [source_options]);

  const handleChangeSortDirection = useCallback(() => {
    setSortDirection((prevDirection) => (prevDirection === 'asc' ? 'desc' : 'asc'));
  }, []);

  const handleKeywordChange = (event, newValue) => {
    setKeyword(newValue?.id_name || newValue);
    setCurrentPage(0);
  };

  useEffect(() => {
    let newFilterModelOptions;
    if (brandFilter && brandFilter !== 'all') {
      newFilterModelOptions = filter(allModelOptions, ['brand', brandFilter]);
    } else {
      newFilterModelOptions = allModelOptions;
    }
    setFilterModelOptions(newFilterModelOptions);
  }, [brandFilter, allModelOptions]);

  return (
    <MKBox
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
      {...props}
    >
      <MKBox display="flex" alignItems="center" width="100%" padding="0.5rem 1rem">
        <MKBox display="flex" flex={1}>
          <ArrowBackIosNewIcon fontSize="medium" style={{ cursor: 'pointer', color: text_color }} onClick={() => navigate('/live_products')} />
        </MKBox>
        <MKBox display="flex" flex={2}>
          <MKTypography width="100%" color={text_color} fontSize={title_font_size} textAlign="center">
            {`Monitors (${totalRows})`}
          </MKTypography>
        </MKBox>
        <MKBox display="flex" justifyContent="flex-end" flex={1}>
          <SettingsOutlinedIcon fontSize="large" style={{ cursor: 'pointer', color: text_color }} onClick={() => navigate('/config_monitors')} />
        </MKBox>
      </MKBox>
      <MKBox
        display="flex"
        width="100%"
        flexDirection="column"
        alignItems="center"
        bgColor={header_color}
        padding="1rem"
      >
        <MKBox
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          marginY={1}
        >
          <MKBox display="flex" bgColor="white" width="100%" alignItems="center" style={{ borderRadius: 10 }}>
            <SearchIcon fontSize="medium" style={{ marginLeft: '1rem', marginRight: '1rem' }} />
            <Autocomplete
              // freeSolo
              options={keywordOptions}
              fullWidth
              getOptionLabel={(option) => {
                if (typeof option === 'string') {
                  return option;
                }
                return `${option.id_name} (${option.brand_name}) (${option.id_code})`;
              }}
              onChange={handleKeywordChange}
              renderInput={(params) => {
                // eslint-disable-next-line no-param-reassign
                params.inputProps.style = { fontSize: '18px' };
                return (
                  <TextField
                    placeholder="Search by brand / model / code"
                    {...params}
                  />
                );
              }}
              filterOptions={multiWordfilterOptions}
            />
          </MKBox>
        </MKBox>
      </MKBox>
      <MKBox display="flex" width="100%" alignItems="center">
        <MKBox width="100%">
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            style={{ backgroundColor: 'transparent' }}
            TabIndicatorProps={{
              style: {
                backgroundColor: '#eacb38',
              },
            }}
            sx={{
              '.MuiButtonBase-root ': {
                color: `${text_color} !important`,
              },
            }}
          >
            <Tab value="sell" label="Sell" />
            <Tab value="new" label="Sell-New" />
            <Tab value="used" label="Sell-Used" />
            <Tab value="buy" label="Wanted" />
          </Tabs>
        </MKBox>
        <MKBox display="flex" alignItems="center" style={{ marginRight: '1rem', cursor: 'pointer' }}>
          <FilterAltOutlinedIcon fontSize="medium" onClick={() => setIsFilterDialogOpen(true)} style={{ color: text_color }} />
          {sortDirection === 'asc'
            ? <KeyboardArrowUpIcon fontSize="small" onClick={handleChangeSortDirection} style={{ color: text_color }} />
            : <KeyboardArrowDownIcon fontSize="small" onClick={handleChangeSortDirection} style={{ color: text_color }} />}
        </MKBox>
      </MKBox>
      <MKBox display="flex" flexDirection="column" width="100%">
        <DataTable
          data={rowsData}
          columns={columns}
          showHeader={show_header}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          rowsPerPage={pageSize}
          rowsPerPageOptions={listSizeOptions(page_options, pageSize)}
          totalCount={totalRows}
          onRowsPerPageChange={setPageSize}
        />
      </MKBox>
      <Dialog
        open={isFilterDialogOpen}
        fullWidth
      >
        <MKBox display="flex" width="100%" justifyContent="flex-end" padding={1}>
          <CloseIcon fontSize="medium" onClick={() => setIsFilterDialogOpen(false)} style={{ cursor: 'pointer' }} />
        </MKBox>
        <DialogContent>
          <MKTypography fontSize="16px" style={{ marginBottom: '0.25rem', marginTop: '1rem' }}>Source Feeder Account(s)</MKTypography>
          <Select
            value={sourceFilter}
            onChange={(v) => setSourceFilter(v)}
            options={sourceOptions}
            fullWidth
            multiple
            style={{ marginBottom: '1rem' }}
          />
          <MKTypography fontSize="16px" style={{ marginBottom: '0.25rem' }}>Brand</MKTypography>
          <Select
            value={brandFilter}
            onChange={(v) => {
              setBrandFilter(v);
              setModelFilter('all');
              setValue(null);
              setInputValue('');
            }}
            options={brandOptions}
            fullWidth
            style={{ marginBottom: '1rem' }}
          />
          <MKTypography fontSize="16px" style={{ marginBottom: '0.25rem' }}>Product</MKTypography>
          <Autocomplete
            // freeSolo
            options={filterModelOptions}
            value={value}
            onChange={(event, newValue) => {
              setModelFilter(newValue?.id_name);
              setValue(newValue);
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setModelFilter(newInputValue?.id_name);
              setInputValue(newInputValue);
            }}
            fullWidth
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return `${option.id_name} (${option.id_code})`;
            }}
            renderInput={(params) => {
              // eslint-disable-next-line no-param-reassign
              params.inputProps.style = { fontSize: '14px', color: '#495057' };
              return (
                <TextField
                  {...params}
                />
              );
            }}
            filterOptions={multiWordfilterOptions}
          />
          <MKTypography fontSize="16px" style={{ marginBottom: '0.25rem' }}>Currency</MKTypography>
          <Select
            value={currencyFilter}
            onChange={(v) => setCurrencyFilter(v)}
            options={currencyOptions}
            fullWidth
            style={{ marginBottom: '1rem' }}
            multiple
          />
          <MKTypography fontSize="16px">Sort By</MKTypography>
          <Select
            value={sortByFilter}
            onChange={(v) => setSortByFilter(v)}
            options={sortByOptions}
            fullWidth
            style={{ marginBottom: '1rem' }}
          />
        </DialogContent>
        <DialogActions>
          <MKBox display="flex" width="100%" justifyContent="space-around">
            <Button fullWidth style={{ marginRight: '1rem', backgroundColor: 'white', borderColor: '1px solid black', color: 'black' }} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button fullWidth style={{ backgroundColor: '#eacb37', color: 'black' }} onClick={handleConfirmFilter}>
              Confirm
            </Button>
          </MKBox>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isProductDialogOpen}
        fullWidth
      >
        {/* <MKBox display="flex" width="100%" justifyContent="flex-end" padding={1}>
          <CloseIcon fontSize="medium" onClick={() => setIsProductDialogOpen(false)} />
        </MKBox> */}
        <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
          <Image src={product?.image_url || product?.model_id?.default_image?.file_url} style={{ maxHeight: '50vh' }} />
          <MKTypography color={text_color} fontWeight="bold" textAlign="center" fontSize="16px">
            {product?.model_id?.id_name}
          </MKTypography>
          <MKTypography color={text_color} textAlign="center" fontSize="16px">
            {product?.description}
          </MKTypography>
          <MKTypography color={text_color} textAlign="center" fontSize="16px">
            {`${product?.currency} ${product?.price.toLocaleString()}`}
          </MKTypography>
          <Button fullWidth style={{ color: 'white', marginTop: '1rem' }} onClick={() => setIsProductDialogOpen(false)}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </MKBox>
  );
};

UserProductListSection.propTypes = {
  title_font_size: PropTypes.string,
  header_color: PropTypes.string,
  text_color: PropTypes.string,
  page_options: PropTypes.string,
  setLoading: PropTypes.func,
  show_header: PropTypes.bool,
  extra_params: PropTypes.object,
  sort_by_options: PropTypes.array,
  currency_options: PropTypes.array,
  source_options: PropTypes.array,
};

UserProductListSection.defaultProps = {
  title_font_size: '',
  header_color: '#2c589d',
  text_color: '#000',
  page_options: [],
  setLoading: null,
  show_header: false,
  extra_params: {},
  sort_by_options: [],
  currency_options: [],
  source_options: [],
};

export default withLoading(UserProductListSection);
